import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo } from "react";
import { CheckCircle2, Loader2, Upload, CreditCard, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { getIcon } from "@/lib/icon-registry";
import { fetchServices, fetchPackages } from "@/lib/content";
import { useIsB2B } from "@/hooks/use-b2b";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { jsonLdScript } from "@/lib/jsonld";

// URL alias → DB service slug matcher + SEO copy. Keeping this map local
// makes the 5 dedicated landing URLs deterministic and independent of DB slug
// changes, while still rendering live services/packages data.
type Alias = {
  urlSlug: string;
  match: (svc: any) => boolean;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  keywords: string[];
};

const ALIASES: Alias[] = [
  {
    urlSlug: "income-tax-return",
    match: (s) => /income[-\s]?tax[-\s]?return/i.test(s.slug || "") || /income tax return/i.test(s.title || ""),
    title: "Income Tax Return Filing Online in Bangladesh — Complete Your E Return BD Easily",
    metaTitle: "E Return BD | E Tax Return Filing Online in Bangladesh (NBR E Return)",
    metaDescription: "Complete your E Return BD easily — file your e tax return online in Bangladesh with NBR-certified consultants. Full NBR e return submission guide, rebate calculation & assessment support.",
    intro: "End-to-end online income tax return preparation and NBR e return submission for individuals, salaried employees, freelancers and companies — a complete e tax return service and tax return filing workflow handled by NBR-experienced consultants.",
    longCopy: [
      "We prepare your income tax return under the Income Tax Act 2023, reconcile TDS challans, apply eligible tax rebate (investment allowance, DPS, life insurance, provident fund) and file the return through the NBR e-Return system — the official online income tax portal for Bangladesh. Think of it as a guided NBR e return submission: you receive the acknowledgement slip, computation sheet and a digital return copy for your records.",
      "Typical documents required for your E Return BD filing: e-TIN certificate, NID, salary certificate / financial statements, bank statements, TDS challans (Form 108), investment proofs, house rent / utility bills and prior-year return. Missing documents? Our team helps you assemble them before submission so your online tax return goes through on the first attempt.",
      "Common use cases for tax return filing with us: individual salaried return, business / proprietorship return, freelancer & remittance return, company (corporate) tax return, revised e tax return and assessment year filing for the 2026–2027 cycle.",
    ],
    keywords: ["e return", "e return bd", "e tax return", "e return nbr", "income tax return filing bangladesh", "online income tax return", "nbr e-return submission"],
  },
  {
    urlSlug: "vat-registration",
    match: (s) => /^vat[-\s]?registration/i.test(s.slug || "") || /vat registration/i.test(s.title || "") || /bin/i.test(s.title || ""),
    title: "Online VAT Registration (BIN) in Bangladesh",
    metaTitle: "Online VAT Registration (BIN) in Bangladesh | Online Tax VAT",
    metaDescription: "Get your 13-digit BIN and VAT registration online in Bangladesh. Mushak 2.1 filing, document review and NBR approval — handled end-to-end by expert consultants.",
    intro: "Register for VAT and obtain your 13-digit Business Identification Number (BIN) online through the NBR VAT Online Project (vat.gov.bd) — with full document review, Mushak 2.1 preparation and post-registration compliance support.",
    longCopy: [
      "Our team files Mushak 2.1 with correct HS codes, business classification and bank information, then follows up with the VAT circle until your digital BIN certificate is issued. Standard turnaround is 3–5 working days once documents are complete.",
      "Documents required: trade license, e-TIN, NID of proprietor / directors, bank solvency certificate, rental deed / ownership proof, MoA & AoA (for companies) and utility bill. We validate every document before submission to avoid rejection.",
      "After registration we can set up monthly Mushak 9.1 return filing, Mushak 6.3 money receipts and input VAT reconciliation so you stay compliant from day one.",
    ],
    keywords: ["online vat registration bd", "bin registration bangladesh", "mushak 2.1 filing", "vat online project"],
  },
  {
    urlSlug: "tin-registration",
    match: (s) => /tin/i.test(s.slug || "") || /tin/i.test(s.title || ""),
    title: "e-TIN Registration & Recovery in Bangladesh",
    metaTitle: "Apply for E-TIN Certificate Online in Bangladesh | Quick Etin Registration",
    metaDescription: "Apply for an e-tin certificate online in Bangladesh — quick etin registration, recovery and download support from NBR-experienced income tax consultants.",
    intro: "Apply for an e-tin certificate online with expert income tax assistance — quick etin registration, secure income tax processing and NBR e-TIN recovery for individuals, freelancers and businesses across Bangladesh.",
    longCopy: [
      "We handle new e-tin registration, lost TIN recovery, name / address / category correction and re-registration for individuals who forgot their credentials. Every submission is made directly through the official NBR e-TIN portal so your e-tin certificate is verifiable, downloadable and audit-safe for future income tax use.",
      "You need an e-tin certificate for salary tax deduction, opening a bank account, obtaining a trade license, buying a car or property above the tax threshold, applying for a credit card and filing your annual income tax return — one etin, many compliance touchpoints.",
      "Documents required: NID / smart card, active mobile number, personal email, business details (for company TIN) and previous TIN — if any — for recovery cases. Our team also walks you through how to download your e-tin certificate PDF from the NBR portal after issuance.",
    ],
    keywords: ["apply for e-tin certificate online", "quick etin registration", "e-tin certificate download", "nbr etin", "income tax registration bangladesh", "tin recovery"],
  },
  {
    urlSlug: "rjsc-company-registration",
    match: (s) => /company[-\s]?tax|rjsc|company[-\s]?registration/i.test(s.slug || "") || /company tax|rjsc|company registration|company formation/i.test(s.title || ""),
    title: "RJSC Company Registration & Company Tax Filing",
    metaTitle: "RJSC Company Registration & Company Tax | Online Tax VAT",
    metaDescription: "Register a private limited company at RJSC in Bangladesh and file corporate tax returns online. Name clearance, MoA/AoA, incorporation & annual return support.",
    intro: "Full-service RJSC company registration and company tax filing — from name clearance and incorporation to annual return submission and corporate income tax return.",
    longCopy: [
      "We handle RJSC name clearance, MoA and AoA drafting, digital signature certificate (DSC), incorporation filing (Schedule X), share allotment and post-incorporation compliance for private limited, one person and partnership entities in Bangladesh.",
      "On the tax side we prepare and file your company income tax return, corporate audit-linked return, advance tax computation, TDS returns and RJSC annual return so the entity stays in good standing with both NBR and RJSC.",
      "Documents required: proposed company name, directors' NID & e-TIN, passport-size photos, registered address, capital structure, bank account details and, for corporate directors, board resolution and constitutional documents.",
    ],
    keywords: ["rjsc company registration bangladesh", "limited company formation dhaka", "company tax filing bd", "rjsc annual return"],
  },
  {
    urlSlug: "trade-license",
    match: (s) => /trade[-\s]?license/i.test(s.slug || "") || /trade license/i.test(s.title || ""),
    title: "Trade License Support in Bangladesh (DSCC, DNCC & More)",
    metaTitle: "Trade License Support in Bangladesh | Online Tax VAT",
    metaDescription: "Apply for a new trade license or renew it online across DSCC, DNCC and other city corporations. Document review, form fill-up and follow-up until issuance.",
    intro: "New trade license application, renewal, category correction and duplicate copy service for DSCC, DNCC and other city corporations and union parishads across Bangladesh.",
    longCopy: [
      "We map your business activity to the correct trade license category, prepare the application form, coordinate with the ward office and follow up until your license is issued. We also handle renewal reminders, category upgrade (e.g. sole proprietorship → partnership) and duplicate copy requests.",
      "Documents required: e-TIN, NID of proprietor / directors, rental deed or ownership documents, utility bill, passport-size photos, partnership deed (for firms) or incorporation certificate (for companies) and prior-year trade license (for renewal).",
      "A valid trade license is a legal prerequisite for VAT registration, opening a business bank account, importing goods and participating in tenders — we make sure the classification you pick supports all of these downstream activities.",
    ],
    keywords: ["trade license online bangladesh", "dscc trade license", "dncc trade license", "trade license renewal"],
  },
];

const servicesQueryOptions = queryOptions({
  queryKey: ["services"],
  queryFn: () => fetchServices(),
  staleTime: 5 * 60 * 1000,
});
const packagesQueryOptions = queryOptions({
  queryKey: ["packages"],
  queryFn: () => fetchPackages(),
  staleTime: 5 * 60 * 1000,
});

function findAlias(urlSlug: string) {
  return ALIASES.find((a) => a.urlSlug === urlSlug) ?? null;
}

export const Route = createFileRoute("/services_/$slug")({
  head: ({ params }) => {
    const alias = findAlias(params.slug);
    if (!alias) {
      return {
        meta: [
          { title: "Service Not Found | Online Tax VAT Bangladesh" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `https://onlinetaxvat.com/services/${alias.urlSlug}`;
    return {
      meta: [
        { title: alias.metaTitle },
        { name: "description", content: alias.metaDescription },
        { name: "keywords", content: alias.keywords.join(", ") },
        { property: "og:title", content: alias.metaTitle },
        { property: "og:description", content: alias.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: alias.metaTitle },
        { name: "twitter:description", content: alias.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: alias.title, path: `/services/${alias.urlSlug}` },
        ]),
        webPageScript({
          path: `/services/${alias.urlSlug}`,
          name: alias.title,
          description: alias.metaDescription,
        }),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${url}#service`,
          name: alias.title,
          serviceType: alias.title,
          description: alias.metaDescription,
          provider: { "@id": "https://onlinetaxvat.com/#organization" },
          areaServed: { "@type": "Country", name: "Bangladesh" },
          availableLanguage: ["en", "bn"],
          url,
          category: "Tax & VAT Consultancy",
        }),
        ...(alias.urlSlug === "income-tax-return"
          ? [
              jsonLdScript({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "@id": `${url}#faq`,
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "How do I file my income tax return online in Bangladesh?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "You can file your income tax return online through the NBR e-Return portal. We prepare the computation under the Income Tax Act 2023, apply eligible rebates and submit the return, then share the acknowledgement slip and a digital copy for your records.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "What documents are required for E Return BD submission?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "You typically need your e-TIN certificate, NID, salary certificate or financial statements, bank statements, TDS challans (Form 108), investment proofs, house rent / utility bills and your previous year's return.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Who must submit an e tax return with the NBR?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Salaried individuals above the tax-free threshold, freelancers with remittance income, business owners, proprietors and companies with an active TIN are all required to submit an annual e tax return through the NBR e-Return system.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Can I file a revised return if I made a mistake?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes. A revised income tax return can be filed within the timeframe allowed by the NBR. We review the original submission, correct the computation and re-file through the e-Return portal.",
                    },
                  },
                ],
              }),
            ]
          : []),
        ...(alias.urlSlug === "tin-registration"
          ? [
              jsonLdScript({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "@id": `${url}#faq`,
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "How do I apply for an e-tin certificate online in Bangladesh?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "You can apply for an e-tin certificate online through the NBR e-TIN portal. Our team handles quick etin registration end-to-end — form fill-up, NID / mobile verification and category selection — and shares the downloadable e-tin certificate once issued.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How to download E-TIN certificate from the NBR portal?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "After registration, sign in to the NBR e-TIN portal, open the 'View TIN Certificate' section and download the PDF. We share step-by-step guidance so you can retrieve your e-tin certificate any time for bank, salary or income tax use.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Do I need an e-tin certificate to file an income tax return?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes. An active e-tin is required to file your annual income tax return, submit E Return BD online and to receive expert income tax assistance for salary, business or freelance income.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Can you recover my lost or forgotten e-TIN?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes. We handle NBR e-TIN recovery for forgotten credentials, mismatched NID information and category corrections so you can resume secure income tax processing without re-registering.",
                    },
                  },
                ],
              }),
            ]
          : []),
      ],
    };
  },
  loader: async ({ params, context }) => {
    const alias = findAlias(params.slug);
    if (!alias) throw notFound();
    await Promise.all([
      context.queryClient.ensureQueryData(servicesQueryOptions),
      context.queryClient.ensureQueryData(packagesQueryOptions),
    ]);
    return { aliasSlug: alias.urlSlug };
  },
  component: ServiceDetailPage,
  notFoundComponent: () => (
    <SiteShell>
      <div className="container-page py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Service not found</h1>
        <p className="text-muted-foreground mt-3">The service you're looking for doesn't exist.</p>
        <Button asChild className="mt-6"><Link to="/services">View all services</Link></Button>
      </div>
    </SiteShell>
  ),
  pendingComponent: () => (
    <SiteShell>
      <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
    </SiteShell>
  ),
});

function ServiceDetailPage() {
  const { slug } = Route.useParams();
  const { t, i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const { isB2B } = useIsB2B();
  const { data: services } = useSuspenseQuery(servicesQueryOptions);
  const { data: packages } = useSuspenseQuery(packagesQueryOptions);

  const alias = findAlias(slug)!;
  const matchedService = useMemo(
    () => (services as any[]).find((s) => alias.match(s)) ?? null,
    [services, alias],
  );
  const relatedPackages = useMemo(() => {
    if (!matchedService) return [] as any[];
    return (packages as any[]).filter((p) => p.services?.slug === matchedService.slug);
  }, [packages, matchedService]);

  const Icon = matchedService ? getIcon(matchedService.icon) : ShieldCheck;
  const displayTitle = isBn && matchedService?.title_bn ? matchedService.title_bn : (matchedService?.title || alias.title);

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-16">
        <div className="container-page">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-foreground">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{alias.title}</span>
          </nav>

          <h1 className="sr-only">{alias.title}</h1>
          <SectionHeading
            eyebrow="Dedicated Service"
            eyebrowClassName="!text-white !bg-primary/30 !border-primary/40"
            title={<><span className="gradient-text">{displayTitle}</span></>}
            description={alias.intro}
          />

          {/* Featured service card — same visual language as /services */}
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="md:col-span-1">
              <div className="group relative h-full rounded-2xl p-6 flex flex-col bg-[#0f5132] border border-[#0f5132] shadow-[0_10px_30px_-12px_rgba(15,81,50,0.55)]">
                <div className="h-12 w-12 rounded-full bg-[#0a3d24] flex items-center justify-center text-emerald-300 ring-1 ring-emerald-400/30">
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="mt-4 font-display font-extrabold text-lg text-white">{displayTitle}</h2>
                <p className="mt-1.5 text-sm text-emerald-100/85">{alias.intro}</p>
                <div className="mt-auto pt-5 border-t border-emerald-400/25 flex flex-col gap-2">
                  <Link to="/upload" search={{ service: matchedService?.slug ?? "" } as any} preload="intent"
                    className="inline-flex items-center justify-center rounded-md bg-emerald-400 text-emerald-950 px-3 py-1.5 text-xs font-bold hover:bg-emerald-300 transition-colors">
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    {t("home.servicesSection.uploadDocs", { defaultValue: "Upload Documents" })}
                  </Link>
                  <Link to="/packages" search={{ service: matchedService?.slug ?? "" } as any} preload="intent"
                    className="inline-flex items-center justify-center rounded-md bg-white text-[#0f5132] px-3 py-1.5 text-xs font-bold hover:bg-emerald-50 transition-colors">
                    <CreditCard className="h-3.5 w-3.5 mr-1" />
                    {t("services.choosePackage")}
                  </Link>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 space-y-4">
              {alias.longCopy.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {para}
                </p>
              ))}
              <div className="flex flex-wrap gap-2 pt-2">
                {alias.keywords.map((k) => (
                  <span key={k} className="inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                    <Sparkles className="h-3 w-3 mr-1" />{k}
                  </span>
                ))}
              </div>
              {alias.urlSlug === "tin-registration" && (
                <div className="pt-4">
                  <h3 className="font-display text-lg md:text-xl font-bold text-foreground">
                    How to Download E-TIN Certificate
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                    Once your quick etin registration is approved, sign in to the NBR e-TIN portal, open the “View TIN Certificate” section and download the PDF copy of your e-tin certificate. Keep a saved copy for salary tax deduction, bank KYC, trade license and annual income tax return filing — our consultants provide expert income tax assistance if you need help retrieving or reprinting it.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Packages for this service */}
          <div className="mt-14">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold mb-2">
              Pricing packages for {displayTitle}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Transparent BDT pricing. Every package is delivered fully online with a signed digital receipt.
            </p>
            {relatedPackages.length === 0 ? (
              <div className="rounded-2xl border p-8 text-center">
                <p className="text-muted-foreground">No packages published yet for this service.</p>
                <Button asChild className="mt-4"><Link to="/packages">View all packages</Link></Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {relatedPackages.map((p: any) => {
                  const nameAll = `${p.name || ""} ${p.name_bn || ""}`.toLowerCase();
                  const isPremium = /premium|প্রিমিয়াম/i.test(nameAll);
                  const cardClass = p.popular
                    ? "ring-[3px] ring-emerald-700 dark:ring-emerald-500 shadow-[0_18px_50px_-12px_rgba(5,150,105,0.45)] bg-gradient-to-b from-emerald-50/90 to-white dark:from-emerald-900/30 dark:to-background"
                    : isPremium
                    ? "border border-amber-300/60 dark:border-amber-400/30 shadow-[0_18px_50px_-18px_rgba(217,119,6,0.45)] bg-gradient-to-br from-amber-50 via-white to-amber-100/60 dark:from-amber-500/10 dark:via-background dark:to-amber-400/10"
                    : "glass";
                  const fmt = (raw: any) => { const n = Number(raw); return Number.isFinite(n) && String(raw).trim() !== "" ? `৳ ${n.toLocaleString()}` : String(raw ?? ""); };
                  return (
                    <div key={p.id} className={`relative rounded-3xl p-6 flex flex-col shadow-card ${cardClass}`}>
                      {p.popular && (
                        <span className="popular-badge">
                          <span aria-hidden className="text-base">★</span>
                          {t("packagesPage.mostPopular")}
                        </span>
                      )}
                      <p className="relative z-10 text-xs uppercase tracking-wider text-muted-foreground">{isBn && p.services?.title_bn ? p.services.title_bn : p.services?.title}</p>
                      <h3 className="relative z-10 font-display font-bold text-xl mt-1">{isBn && p.name_bn ? p.name_bn : p.name}</h3>
                      {(isBn && p.description_bn) || p.description ? <p className="text-xs text-muted-foreground mt-1">{isBn && p.description_bn ? p.description_bn : p.description}</p> : null}
                      {isB2B && p.b2b_price ? (
                        <div className="mt-4">
                          <p className="text-sm font-semibold text-red-600 line-through">{fmt(p.price)}</p>
                          <p className="price-display text-emerald-600 dark:text-emerald-400">{fmt(p.b2b_price)}</p>
                        </div>
                      ) : (
                        <p className="price-display gradient-text mt-4">{fmt(p.price)}</p>
                      )}
                      <p className="mt-2 text-sm text-muted-foreground">
                        {t("packagesPage.delivery")} <span className="font-semibold text-foreground">{(isBn && p.delivery_days_bn) ? p.delivery_days_bn : (p.delivery_days || t("packagesPage.defaultDelivery"))}</span>
                      </p>
                      <ul className="relative z-10 mt-5 space-y-2.5 text-sm flex-1">
                        {((isBn && Array.isArray(p.features_bn) && p.features_bn.length > 0 ? p.features_bn : p.features) ?? []).map((f: string, k: number) => (
                          <li key={k} className="flex items-start gap-2">
                            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 mt-0.5">
                              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                            </span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Button asChild className={`relative z-10 mt-6 ${p.popular ? "popular-btn shadow-glow" : ""}`} variant={p.popular ? "default" : "outline"}>
                        <Link to="/upload" search={{ service: p.services?.slug ?? "", package: p.slug, packageId: p.id } as any}>{t("packagesPage.getStarted")}</Link>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cross-links to sibling service pages for internal SEO */}
          <div className="mt-16">
            <h2 className="font-display text-xl md:text-2xl font-extrabold mb-4">Explore other services</h2>
            <div className="flex flex-wrap gap-2">
              {ALIASES.filter((a) => a.urlSlug !== slug).map((a) => (
                <Link
                  key={a.urlSlug}
                  to="/services/$slug"
                  params={{ slug: a.urlSlug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors"
                >
                  {a.title} <ArrowRight className="h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-14 text-center">
            <p className="text-sm text-muted-foreground mb-3">{t("services.ctaQuestion")}</p>
            <Button asChild size="lg" className="gradient-primary text-primary-foreground shadow-glow">
              <Link to="/contact">{t("services.ctaButton")}</Link>
            </Button>
          </div>
          {SERVICE_DETAIL_CONTENT[slug] && (
            <ServiceDetailSection content={SERVICE_DETAIL_CONTENT[slug]} />
          )}
        </div>
      </section>
    </SiteShell>
  );
}
